import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Button, Icon, Menu, Message, Modal, Table } from "semantic-ui-react";
import { useAPIFetchAllLocal } from "../../../core/UseAPILocal";
import BmpGeneralIntermediate from "../../../models/BmpGeneralIntermediate";
import Wave2 from "../../../images/modalWave2.svg";
import './QueueModal.scss';
import { useAuth } from "react-oidc-context";
import LookupFactory from "../../../lookups/LookupFactory";
import { LutQueueStepId } from "../../../models/lutModels/LutQueueStep";
import QueueItemStatusInfo from "../../../models/viewModels/QueueItemModel";
import { BmpChangeViewModel, QueueService } from "../../../services/QueueService";
import { toast } from "react-toastify";

export type QueueModalProps = {
    icon: string; // icon to display example for trash can ```trash alternate outline```
    queueItem: QueueItemStatusInfo;
    theme: string; // primary secondary tertiary white
    lookups: LookupFactory;
    queueError: string;
    refreshQueueList: () => void;
}

export const QueueModal = (props: QueueModalProps) => {
    const auth = useAuth();
    const [showAllRecords, setShowAllRecords] = useState<boolean>(true);
    const [open, setOpen] = React.useState(false);
    const [queueIntermediateList, setQueueIntermediateList] = useState<Array<BmpGeneralIntermediate>>(new Array<BmpGeneralIntermediate>());
    const [fetchingQueueIntermediateList, setFetchingQueueIntermediateList] = useState<boolean>(false);
    const [activeItem, setActiveItem] = useState<"UploadWideErrors" | "Duplicates" | "ProjectErrors">("UploadWideErrors");
    const [showAllDuplicates, setShowAllDuplicates] = useState<boolean>(true);
    const queueService = useMemo(() => new QueueService(auth.user?.access_token), [auth]);
    const [duplicateItems, setDuplicateItems] = useState<BmpChangeViewModel[]>([]);

    //not showing completed and successful uploads because they are inactivated
    useAPIFetchAllLocal(auth.user?.access_token, new BmpGeneralIntermediate(), fetchingQueueIntermediateList, setFetchingQueueIntermediateList, setQueueIntermediateList, { queueItemId: props.queueItem.id, active: 1 }, () => {
    });

    var isDuplicateStep = useMemo(() => {
        return props.lookups.lutQueueStepProcess.find(x => x.id === props.queueItem.queueStepProcessId)!.queueStepId === LutQueueStepId.UserDuplicateInteraction
        && props.queueItem.queueStatusId === 1006; // status must be paused
    }, [props.lookups.lutQueueStepProcess, props.queueItem.queueStatusId, props.queueItem.queueStepProcessId]);

    const getDuplicates = useCallback((queueItemId: number) => {
        queueService.getDuplicateQueueItems(queueItemId)
        .then((result) => {
            setDuplicateItems(result);
        })
        .catch((e) => {
            console.error("Error getting duplicate queue items", e);
            toast.error("Error getting duplicates!");
        });
    }, [queueService])

    const approveDuplicates = useCallback(() => {
        queueService.approveDuplicateQueueItems(props.queueItem.id)
        .then(() => {
            toast.success("Duplicates approved successfully!");
            props.refreshQueueList();
        })
        .catch((e) => {
            console.error("Error approving duplicate queue items", e);
            toast.error("Error approving duplicates!");
        });
    }, [queueService, props]);

    const cancelUpload = useCallback(() => {
        queueService.cancelUpload(props.queueItem.id)
        .then(() => {
            toast.success("Upload canceled successfully!");
            props.refreshQueueList();
        })
        .catch((e) => {
            console.error("Error canceling upload", e);
            toast.error("Error canceling upload!");
        });
    }, [queueService, props]);

    useEffect(() => {
        if (open && !isDuplicateStep && !queueIntermediateList.some((x) => x.queueErrorDescription)) {
            //if there are no errors tied to specific bmps then its a general error with the queue item so dont show the table
            setActiveItem("UploadWideErrors");
        }
        else if (open && isDuplicateStep) {
            setActiveItem("Duplicates");
        }
    }, [fetchingQueueIntermediateList, isDuplicateStep, open, queueIntermediateList]);

    useEffect(() => {
        if (open && props.queueItem.id) {
            setFetchingQueueIntermediateList(true);
            getDuplicates(props.queueItem.id);
        }
    }, [props.queueItem.id, open, getDuplicates]);

    return (
        <Modal className="queue-modal-component mb-2"
            size="large"
            centered={true}
            open={open}
            onClose={() => setOpen(false)}
            onOpen={() => setOpen(true)}
            trigger={
                isDuplicateStep
                ? <div className="duplicate-check-button">Review Duplicates</div>
                : <Icon className={`${props.icon} ${props.theme}`
            } />}
        >
            <Modal.Header>
                <div className="queue-modal-title">Project Error List</div>
                <div className="queue-modal-title-subtext">Please see the Project Upload Instructions available on the Upload Project Data tab for additional support on resolving upload errors.</div>
            </Modal.Header>
            <Modal.Content>
                <div style={{ marginBottom: '1rem'}}>
                    <Menu pointing secondary>
                        <Menu.Item
                            name="Duplicates"
                            active={activeItem === "Duplicates"}
                            onClick={() => setActiveItem("Duplicates")}
                        >
                            Duplicates
                            {isDuplicateStep &&
                                <Icon name="bell" color="red" />
                            }
                        </Menu.Item>
                        <Menu.Item
                            name="Upload-Wide Errors"
                            active={activeItem === "UploadWideErrors"}
                            onClick={() => setActiveItem("UploadWideErrors")}
                            content="Upload-Wide Errors"
                        />
                        <Menu.Item
                            name="Project Errors"
                            active={activeItem === "ProjectErrors"}
                            onClick={() => setActiveItem("ProjectErrors")}
                            content="Project Errors"
                        />
                    </Menu>
                </div>
                <div style={{ marginTop: '1rem', marginLeft: '1rem', marginRight: '1rem' }}>
                    {activeItem === "Duplicates" &&
                        <div className="table-wrapper">
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <Button size="mini" onClick={() => setShowAllDuplicates(!showAllDuplicates)}>{showAllDuplicates ? "Show only changed BMPs" : "Show All Duplicates"}</Button>
                                {isDuplicateStep && <>
                                        <Button
                                            size="medium"
                                            onClick={approveDuplicates}
                                            color="green"
                                            children={"Approve Duplicates"}
                                            style={{ marginLeft: 'auto' }}
                                        />
                                        <Button
                                            size="medium"
                                            onClick={cancelUpload}
                                            color="red"
                                            children={"Cancel Upload"}
                                        />
                                </>}
                            </div>
                            <div className="modal-scrollable">
                                <Table celled fixed>
                                    <Table.Header>
                                        <Table.Row>
                                            <Table.HeaderCell width={2}>Row</Table.HeaderCell>
                                            <Table.HeaderCell width={5}>BMP Name</Table.HeaderCell>
                                            <Table.HeaderCell width={5}>Property</Table.HeaderCell>
                                            <Table.HeaderCell width={4}>Old Value</Table.HeaderCell>
                                            <Table.HeaderCell width={4}>New Value</Table.HeaderCell>
                                        </Table.Row>
                                    </Table.Header>
                                    <Table.Body>
                                        {duplicateItems
                                            .filter(x => x.bmpGeneralId !== null) // if its null then its a new bmp
                                            .filter(x => showAllDuplicates || x.changes.length > 0)
                                            .map((item, i) => {
                                                if (item.changes.length === 0) {
                                                    return (<Table.Row key={`${item.bmpGeneralId}-${i}`}>
                                                            <Table.Cell>{item.rowNumber}</Table.Cell>
                                                            <Table.Cell>{item.bmpName}</Table.Cell>
                                                            <Table.Cell><i style={{ color: "gray"}}>No Changes</i></Table.Cell>
                                                            <Table.Cell></Table.Cell>
                                                            <Table.Cell></Table.Cell>
                                                        </Table.Row>)
                                                }
                                                else {
                                                    return item.changes.map((change, j) => {
                                                        return (<Table.Row key={`${item.bmpGeneralId}-${i}-${j}`}>

                                                            {j === 0 && <>
                                                                <Table.Cell rowSpan={item.changes.length}>{item.rowNumber}</Table.Cell>
                                                                <Table.Cell rowSpan={item.changes.length}>{item.bmpName}</Table.Cell>
                                                            </>}
                                                            <Table.Cell>{change.property}</Table.Cell>
                                                            <Table.Cell>{change.previousValue}</Table.Cell>
                                                            <Table.Cell>{change.newValue}</Table.Cell>
                                                        </Table.Row>)
                                                    })
                                                }
                                            })
                                        }
                                    </Table.Body>
                                </Table>
                            </div>
                            {showAllDuplicates && duplicateItems.filter(x => x.bmpGeneralId !== null).length === 0 &&
                                <Message content="No duplicates were found" />
                            }
                            {!showAllDuplicates && duplicateItems.filter(x => x.bmpGeneralId !== null).filter(x => x.changes.length > 0).length === 0 &&
                                <Message content="No duplicates with changes were found" />
                            }
                        </div>
                    }
                    {activeItem === "UploadWideErrors" &&
                        <div className="modal-scrollable">
                            {props.queueError?.trim().split('\n').map((item, key) => {
                                return <Fragment key={key}>{item}<br /></Fragment>
                            })}
                        </div>
                    }
                    {activeItem === "ProjectErrors" &&
                        <div className="table-wrapper">
                            <Button size="mini" onClick={() => setShowAllRecords(!showAllRecords)}>{showAllRecords ? "Show only messages" : "Show all"}</Button>
                            <div className="modal-scrollable">
                                <Table celled fixed>
                                    <Table.Header>
                                        <Table.Row>
                                            <Table.HeaderCell width={1}>Row</Table.HeaderCell>
                                            <Table.HeaderCell width={5}>BMP Name</Table.HeaderCell>
                                            <Table.HeaderCell width={2}>Error Type</Table.HeaderCell>
                                            <Table.HeaderCell width={8}>Error Description</Table.HeaderCell>
                                        </Table.Row>
                                    </Table.Header>
                                    <Table.Body>
                                        {queueIntermediateList
                                            .filter(x => showAllRecords || x.queueErrorDescription)
                                            .map((item: BmpGeneralIntermediate, index: number) => {
                                                return (<Table.Row key={`${index} + ${item.id}`}>
                                                    <Table.Cell>{item.rowId}</Table.Cell>
                                                    <Table.Cell>{item.name}</Table.Cell>
                                                    <Table.Cell className={!item.queueErrorDescription ? "queue-error-none" : ""}>{props.lookups && item.queueErrorTypeId ? props.lookups.getQueueErrorTypeById(item.queueErrorTypeId) : "None"}</Table.Cell>
                                                    <Table.Cell className={!item.queueErrorDescription ? "queue-error-none" : ""}>{item.queueErrorDescription ?? "None"}</Table.Cell>
                                                </Table.Row>)
                                            })
                                        }
                                    </Table.Body>
                                </Table>
                            </div>
                                {queueIntermediateList.filter(x => showAllRecords || x.queueErrorDescription).length === 0 &&
                                    <Message content="No project errors to display." />
                                }
                        </div>
                    }
                </div>
            </Modal.Content>
            <Modal.Actions>
                <div className="wave-div">
                    <img src={Wave2} alt="wave" className="wave" />
                </div>
                <div className="btn-wrapper">
                    <Button color="grey" onClick={() => setOpen(false)}>Close</Button>
                </div>
            </Modal.Actions>
        </Modal>
    )
}
